import { useState } from 'react';
import { post } from '../../../Services/ApiClient';
import useApiClient from '../../Base/Hooks/useApiClient';
import { useEffectAsync } from '../../../helpers';
import {
	WizardAction,
	WizardFinalStep,
	WizardRootItem,
} from '../Interfaces/Wizard.interface';
import { isArray } from 'lodash';

export default function useWizard({
	key,
	initialData,
	onWizardDone,
}: {
	key: string;
	initialData?: object;
	onWizardDone: (action?: WizardAction) => void;
}) {
	const [activeStep, setActiveStep] = useState<WizardRootItem | null>(null);
	const { displayError } = useApiClient();

	const wizardPost = async (values?: object) => {
		try {
			const result = await post(`/wizard/${key}/tree`, values);
			return isArray(result.data)
				? (result.data as WizardRootItem[])
				: (result.data as WizardFinalStep);
		} catch (e) {
			displayError(e);
		}
	};

	const isWizardFinalStep = (v: unknown): v is WizardFinalStep => {
		return (
			!!v && typeof v === 'object' && 'key' in (v as Record<string, unknown>)
		);
	};

	const submitStep = async (values: object) => {
		const wizard = await wizardPost({ ...values, step: activeStep?.key });
		if (!wizard) return;

		if (isArray(wizard) && wizard.length > 0) {
			const step = wizard[0];
			if (step.key === 'done') {
				onWizardDone(step.settings?.action);
			} else {
				setActiveStep(wizard[0]);
			}
			return;
		}

		if (isWizardFinalStep(wizard)) {
			if (wizard.key === 'done') {
				onWizardDone(wizard.settings?.action);
			}
		}
	};

	useEffectAsync(async () => {
		await submitStep(initialData || {});
	}, []);

	return {
		activeStep,
		submitStep,
	};
}
